import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../api';
import type { AuthState, User } from '../types';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login({ username, password });

          // Single-user system - we know the user info
          const user: User = {
            id: '1',
            username: username,
            is_active: true,
          };

          set({
            token: response.access_token,
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // Only reset isLoading, don't clear other state (already on login page)
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ token: null, user: null, isAuthenticated: false });
      },

      checkAuth: async () => {
        const { token, user } = get();
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        try {
          // Validate token with backend
          const response = await authApi.validateToken();
          if (response.valid) {
            // Keep existing user or reconstruct from response
            set({
              user: user || { id: '1', username: response.username, is_active: true },
              isAuthenticated: true
            });
          } else {
            set({ token: null, user: null, isAuthenticated: false });
          }
        } catch {
          set({ token: null, user: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
